import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import './DashboardPage.css';

export default function DashboardPage() {
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get('/courses/your-first-rental-property'),
      api.get('/courses/your-first-rental-property/progress').catch(() => null),
    ]).then(([cRes, pRes]) => {
      setCourse(cRes.data);
      setProgress(pRes?.data || null);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-center"><div className="spinner"/></div>;

  const enrolled = progress?.enrolled;
  const pct = progress?.percent || 0;
  const completedIds = new Set(progress?.completed_lesson_ids || []);
  const doneMods = course?.modules?.filter(m =>
    m.lessons?.every(l => completedIds.has(l.id))
  ).length || 0;

  // Find current lesson (first incomplete)
  let curMod = null, curLesson = null;
  if (enrolled && course?.modules) {
    outer: for (const mod of course.modules) {
      for (const lesson of (mod.lessons || [])) {
        if (!completedIds.has(lesson.id)) { curMod = mod; curLesson = lesson; break outer; }
      }
    }
  }

  // Module unlock: sequential
  const isUnlocked = (modIdx) => {
    if (modIdx === 0) return true;
    const prev = course.modules[modIdx - 1];
    return prev?.lessons?.every(l => completedIds.has(l.id));
  };

  return (
    <div className="dash-page">
      {/* SIDEBAR */}
      <aside className="dash-sidebar hide-mobile">
        <div className="dash-sb-logo">
          PropertyTraining<span>.ai</span>
          <small>Student Portal</small>
        </div>
        <nav className="dash-sb-nav">
          <div className="dash-sb-item active">📊 My Dashboard</div>
          <div className="dash-sb-item" onClick={() => nav('/courses')}>📚 All Courses</div>
          <div className="dash-sb-item" onClick={() => nav('/certificates')}>🏆 My Certificates</div>
        </nav>
        {course && (
          <div className="dash-sb-mods">
            <div className="dash-sb-section">Course 1 Modules</div>
            {course.modules?.map((m, i) => {
              const done = m.lessons?.every(l => completedIds.has(l.id));
              const curr = m.id === curMod?.id;
              const ul = isUnlocked(i);
              return (
                <div key={m.id} className={`dash-sb-mod ${curr ? 'active' : ''} ${done ? 'done' : ''}`}
                  onClick={() => ul && enrolled && nav(`/learn/your-first-rental-property/${m.id}`)}>
                  <span className={`dash-sb-dot ${done ? 'dot-done' : curr ? 'dot-curr' : 'dot-lock'}`}>
                    {done ? '✓' : i + 1}
                  </span>
                  <span className="dash-sb-mod-name">{m.title}</span>
                </div>
              );
            })}
          </div>
        )}
      </aside>

      {/* MAIN */}
      <main className="dash-main">
        <div className="dash-greeting">
         
          <p>{enrolled ? 'Keep going — you\'re making great progress.' : 'Enrol in a course to start learning.'}</p>
        </div>

        {enrolled && course && (
          <div className="dash-progress-card">
            <div className="dash-prog-avatar">👩‍🏫</div>
            <div className="dash-prog-info">
              <div className="dash-prog-label">Your First Rental Property</div>
              <div className="dash-prog-title">
                {curMod ? `Module ${course.modules.indexOf(curMod) + 1} — ${curMod.title}` : '🏆 Course Complete!'}
              </div>
              <div className="progress-bar" style={{marginBottom:'8px'}}>
                <div className="progress-fill" style={{width: pct + '%'}}/>
              </div>
              <div className="dash-prog-meta">
                <span>{pct}% complete</span>
                {curLesson && <span className="dash-prog-next" onClick={() => nav(`/learn/your-first-rental-property/${curMod.id}/${curLesson.id}`)}>Resume →</span>}
              </div>
            </div>
            {curMod && <button className="btn btn-primary" onClick={() => nav(`/learn/your-first-rental-property/${curMod.id}/${curLesson?.id || ''}`)}>Continue ▶</button>}
          </div>
        )}

        {!enrolled && (
          <div className="dash-enrol-cta card card-p">
            <h3>Start your property learning journey</h3>
            <p>Enrol in Course 1 and get lifetime access to 10 modules, 30 lessons, and Amanda AI.</p>
            <Link to="/courses/your-first-rental-property" className="btn btn-primary mt-2">Enrol Now — £98 →</Link>
          </div>
        )}

        {enrolled && (
          <div className="dash-stats">
            <div className="dash-stat card card-p"><div className="dash-stat-val">{doneMods}</div><div className="dash-stat-label">Modules Complete</div></div>
            <div className="dash-stat card card-p"><div className="dash-stat-val">{completedIds.size}</div><div className="dash-stat-label">Lessons Done</div></div>
            <div className="dash-stat card card-p"><div className="dash-stat-val">{pct}%</div><div className="dash-stat-label">Course Progress</div></div>
          </div>
        )}

        {course && (
          <>
            <h3 style={{marginBottom:'16px'}}>Course Modules</h3>
            <div className="dash-mod-list">
              {course.modules?.map((m, i) => {
                const done = m.lessons?.every(l => completedIds.has(l.id));
                const curr = m.id === curMod?.id;
                const ul = enrolled && isUnlocked(i);
                const doneL = m.lessons?.filter(l => completedIds.has(l.id)).length || 0;
                return (
                  <div key={m.id} className={`dash-mod-card card ${!ul ? 'locked' : 'card-hover'}`}
                    onClick={() => ul && nav(`/learn/your-first-rental-property/${m.id}`)}>
                    <div className={`dash-mod-num ${done ? 'num-done' : curr ? 'num-curr' : 'num-lock'}`}>
                      {done ? '✓' : i + 1}
                    </div>
                    <div className="dash-mod-info">
                      <div className="dash-mod-title">Module {i + 1} — {m.title}</div>
                      <div className="dash-mod-sub">
                        {ul ? `${doneL} of ${m.lessons?.length || 0} lessons complete` : `Complete Module ${i} to unlock`}
                      </div>
                    </div>
                    <span className={`badge ${done ? 'badge-success' : curr ? 'badge-teal' : 'badge-grey'}`}>
                      {done ? 'Complete' : curr ? 'In Progress' : ul ? 'Start' : '🔒 Locked'}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
